export const USER_AGENT = typeof navigator === 'undefined' ? '' : navigator.userAgent;

const isUserAgentMatch = (param: string) => {
  const reg = new RegExp(param, 'i');
  return reg.test(USER_AGENT);
};

const getBrowserVersion = (param: string): string => {
  if (isUserAgentMatch(param)) {
    const reg = new RegExp(`${param}\\/([\\d.]+)`);
    const match = USER_AGENT.match(reg);
    if (match && match[1]) {
      return match[1];
    }
  }
  return '';
};

const getBrowserMajorVersion = (param: string): number => {
  if (isUserAgentMatch(param)) {
    const reg = new RegExp(`${param}\\/(\\d+)`);
    const match = USER_AGENT.match(reg);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }
  }
  return NaN;
};

const webkitVersionMap = /AppleWebKit\/([\d.]+)/i.exec(USER_AGENT);
const appleWebkitVersion = webkitVersionMap ? parseFloat(webkitVersionMap[1]) : NaN;


// iOS: ----------------------------------------------------
export const IS_IPAD = isUserAgentMatch('iPad');

// iPad Pro 的 safari ua 和 mac safari 一样，此处无法通过 ua 判断，得通过触点数量辅助判断。
// ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view?bug_id=1020396022094087239
export const IS_IPAD_PRO = typeof navigator !== 'undefined' &&
  navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && isUserAgentMatch('Macintosh');

// The Facebook app's UIWebView identifies as both an iPhone and iPad, so
// to identify iPhones, we need to exclude iPads.
// http://artsy.github.io/blog/2012/10/18/the-perils-of-ios-user-agent-sniffing/
export const IS_IPHONE = isUserAgentMatch('iPhone') && !IS_IPAD;
export const IS_IPOD = isUserAgentMatch('iPod');
export const IS_IOS = IS_IPHONE || IS_IPAD || IS_IPOD || IS_IPAD_PRO;
// 剔除掉模拟器的情况
export const isRealIOS = () => {
  try {
    return IS_IOS && navigator.maxTouchPoints > 1 && navigator.vendor.includes('Apple');
  } catch (error) {
    return IS_IOS;
  }
};

export const IS_ANDROID = isUserAgentMatch('Android');

export const ANDROID_VERSION = (function () {
  if (IS_ANDROID) {
    // This matches Android Major.Minor.Patch versions
    // ANDROID_VERSION is Major.Minor as a Number, if Minor isn't available, then only Major is returned
    const match = USER_AGENT.match(/Android (\d+)(?:\.(\d+))?(?:\.(\d+))*/i);
    if (match) {
      const major = match[1] && parseFloat(match[1]);
      const minor = match[2] && parseFloat(match[2]);

      if (major && minor) {
        return parseFloat(`${match[1]}.${match[2]}`);
      }
      if (major) {
        return major;
      }
    }
  }
  return NaN;
}());

// Old Android is defined as Version older than 2.3, and requiring a webkit version of the android browser
export const IS_OLD_ANDROID = IS_ANDROID && isUserAgentMatch('webkit') && ANDROID_VERSION < 2.3;
export const IS_NATIVE_ANDROID = IS_ANDROID && ANDROID_VERSION < 5 && appleWebkitVersion < 537;

// Firefox
export const IS_FIREFOX = isUserAgentMatch('Firefox');
export const FIREFOX_VERSION = getBrowserVersion('Firefox');
export const FIREFOX_MAJOR_VERSION = getBrowserMajorVersion('Firefox');

// old Edge
export const IS_EDGE = isUserAgentMatch('Edge');
export const EDGE_VERSION = getBrowserVersion('Edge');

// new Edge
export const IS_EDG = isUserAgentMatch('Edg');
export const EDG_VERSION = getBrowserVersion('Edg');
export const EDG_MAJOR_VERSION = getBrowserMajorVersion('Edg');

// sogou mobile
export const IS_SOGOUM = isUserAgentMatch('SogouMobileBrowser');
export const SOGOUM_VERSION = getBrowserVersion('SogouMobileBrowser');

// sogou desktop
export const IS_SOGOU = isUserAgentMatch('MetaSr\\s');
export const SOGOU_VERSION = getBrowserVersion('MetaSr\\s');

export const IS_TBS = isUserAgentMatch('TBS'); // 仅X5内核，QQ浏览器默认x5内核，但是agent没有TBS
export const TBS_VERSION = getBrowserVersion('TBS');

export const IS_XWEB = isUserAgentMatch('XWEB');
export const XWEB_VERSION = getBrowserVersion('XWEB');

// IE
export const IS_IE8 = isUserAgentMatch('MSIE\\s8\\.0');
export const IS_IE = isUserAgentMatch('MSIE\\/\\d+');
export const IE_VERSION = (function () {
  if (IS_IE) {
    const result = /MSIE\s(\d+)\.\d/.exec(USER_AGENT);
    let version = result && parseFloat(result[1]);
    if (!version && /Trident\/7.0/i.test(USER_AGENT) && /rv:11.0/.test(USER_AGENT)) {
      // IE 11 has a different user agent string than other IE versions
      version = 11.0;
    }
    return version;
  }
  return NaN;
}());

export const IS_WECHAT = isUserAgentMatch('(micromessenger|webbrowser)');
export const WECHAT_VERSION = getBrowserVersion('MicroMessenger');


export const IS_X5MQQB =
  !IS_TBS &&
  isUserAgentMatch('MQQBrowser') &&
  isUserAgentMatch('COVC'); // 移动端 QQ X5 内核浏览器

export const IS_MQQB =
  !IS_TBS &&
  isUserAgentMatch('MQQBrowser') &&
  !isUserAgentMatch('COVC'); // 移动端 QQ 浏览器

export const MQQB_VERSION = (IS_MQQB || IS_X5MQQB) ? getBrowserVersion('MQQBrowser') : '';

export const IS_WQQB = !IS_TBS && isUserAgentMatch(' QQBrowser'); // windows端QQ浏览器

export const WQQB_VERSION = getBrowserVersion(' QQBrowser');

export const IS_MACQQB = !IS_TBS && isUserAgentMatch('QQBrowserLite'); // Mac端QQ浏览器
export const MACQQB_VERSION = getBrowserVersion('QQBrowserLite');


export const IS_IPADQQB = !IS_TBS && isUserAgentMatch('MQBHD'); // iPad端QQ浏览器
export const IPADQQB_VERSION = getBrowserVersion('MQBHD');

export const IS_WIN = isUserAgentMatch('Windows'); // window系统
export const IS_MAC = !IS_IOS && isUserAgentMatch('MAC OS X'); // MAC系统，先检查IOS
export const IS_LINUX = !IS_ANDROID && isUserAgentMatch('Linux');
export const IS_CHROME_OS = isUserAgentMatch('CrOS');
export const IS_WX = isUserAgentMatch('MicroMessenger'); // 是否为微信环境
export const IS_UCBROWSER = isUserAgentMatch('UCBrowser');
export const IS_ELECTRON = isUserAgentMatch('Electron'); // 是否为 electron

export const IS_MIBROWSER = isUserAgentMatch('MiuiBrowser'); // 小米浏览器
export const MI_VERSION = getBrowserVersion('MiuiBrowser');

export const IS_HUAWEIBROWSER = isUserAgentMatch('HuaweiBrowser'); // 华为浏览器
export const IS_HUAWEI = isUserAgentMatch('Huawei') || isUserAgentMatch('HUAWEI'); // 华为设备
export const IS_HONOR = isUserAgentMatch('Honor') || isUserAgentMatch('HONOR'); // 荣耀设备

export const HUAWEI_VERSION = getBrowserVersion('HuaweiBrowser');

export const IS_SAMSUNGBROWSER = isUserAgentMatch('SamsungBrowser'); // 三星浏览器
export const SAMSUNG_VERSION = getBrowserVersion('SamsungBrowser');

export const IS_OPPOBROWSER = isUserAgentMatch('HeyTapBrowser'); // OPPO浏览器
export const OPPO_VERSION = getBrowserVersion('HeyTapBrowser');

export const IS_VIVOBROWSER = isUserAgentMatch('VivoBrowser'); // vivo浏览器
export const VIVO_VERSION = getBrowserVersion('VivoBrowser');

export const IS_OPENHARMONY = isUserAgentMatch('OpenHarmony'); // 华为纯血鸿蒙
export const OPENHARMONY_VERSION = getBrowserVersion('OpenHarmony');

// Chrome
export const getChromeMajorVersion = (): number => getBrowserMajorVersion('Chrome');

export const IS_IOS_CHROME = isUserAgentMatch('CriOS');
export const IS_CHROMIUM_BASE = isUserAgentMatch('Chrome');
export const IS_CHROME =
  !IS_EDGE &&
  !IS_SOGOU &&
  !IS_SOGOUM &&
  !IS_TBS &&
  !IS_XWEB &&
  !IS_EDG &&
  !IS_WQQB &&
  !IS_MIBROWSER &&
  !IS_HUAWEIBROWSER &&
  !IS_SAMSUNGBROWSER &&
  !IS_OPPOBROWSER &&
  !IS_VIVOBROWSER &&
  IS_CHROMIUM_BASE;

export const IS_HEADLESS_CHROME = isUserAgentMatch('HeadlessChrome');
export const CHROME_MAJOR_VERSION = getChromeMajorVersion();
export const IS_CHROMIUM_128_TO_143 = IS_CHROMIUM_BASE && CHROME_MAJOR_VERSION >= 128 && CHROME_MAJOR_VERSION <= 143;
export const CHROME_VERSION = getBrowserVersion('Chrome');
export const ELECTRON_MAJOR_VERSION = getBrowserMajorVersion('Electron');
// Safari
export const IS_SAFARI =
  !IS_CHROMIUM_BASE &&
  !IS_MQQB &&
  !IS_X5MQQB &&
  !IS_MACQQB &&
  !IS_IPADQQB &&
  isUserAgentMatch('Safari');

export const IS_ANY_SAFARI = IS_SAFARI || IS_IOS;
export const SAFARI_VERSION = getBrowserVersion('Version');

// Android Lollipop and Above: webview will be the same as native but it will contain "wv"
// Android KitKat to lollipop webview will put {version}.0.0.0
// 此处判断其实不完全准确，大部分国内移动浏览器基于 chromium 实现，其 ua 字段中也有 wv 字段。例如 360, VIVO 浏览器等。
export const IS_ANDROID_WEBVIEW = /Android.*(wv|.0.0.0)/.test(USER_AGENT);

export const IOS_VERSION = (() => {
  if (IS_IPAD_PRO) {
    return SAFARI_VERSION;
  }
  if (IS_IOS) {
    const match = USER_AGENT.match(/OS (\d+)_(\d+)/i);
    if (match && match[1]) {
      let result = match[1];
      if (match[2]) {
        result += `.${match[2]}`;
      }
      return result;
    }
  }
  return '';
})();
export function isVersionSmallerThan(version: string, targetVersion: string) {
  const versionList = version.split('.').map(v => Number(v));
  const targetVersionList = targetVersion.split('.').map(v => Number(v));
  for (let i = 0; i < Math.max(versionList.length, targetVersionList.length); i++) {
    const ver = versionList[i] || 0;
    const targetVer = targetVersionList[i] || 0;
    if (ver < targetVer) {
      return true;
    }
    if (ver > targetVer) {
      return false;
    }
  }
  return false;
}
export function isVersionLargerThan(version: string, targetVersion: string, allowEqual = false) {
  const versionList = version.split('.').map(v => Number(v));
  const targetVersionList = targetVersion.split('.').map(v => Number(v));
  for (let i = 0; i < Math.max(versionList.length, targetVersionList.length); i++) {
    const ver = versionList[i] || 0;
    const targetVer = targetVersionList[i] || 0;
    if (ver > targetVer) return true;
    if (ver < targetVer) return false;
  }
  // 如果允许等于，则返回 true
  return allowEqual;
}
export const IOS_MAIN_VERSION = Number(IOS_VERSION.split('.')[0]);
export const IS_SAFARI_15_1 = SAFARI_VERSION === '15.1';
export const IS_IOS_15_1 = IOS_VERSION === '15.1';
export const IS_IOS_13_OR_14 = (() => {
  const mainVersion = Number(IOS_VERSION.split('.')[0]);
  return mainVersion === 14 || mainVersion === 13;
})();


// ref: https://chromium.googlesource.com/chromium/src.git/+/master/docs/ios/user_agent.md
export const IS_DESKTOP_IOS_CHROME = IS_IOS_CHROME && SAFARI_VERSION === '11.1.1';

// 是否本地环境 file, localhost, 127.0.0.1
export const IS_LOCAL = typeof location === 'undefined' ? false :
  location.protocol === 'file:' ||
  location.hostname === 'localhost' ||
  location.hostname === '127.0.0.1';

/**
 * 检测 LocalStorage 是否可用
 * @export
 * @return {Boolean}
 */
export const isLocalStorageEnabled = (() => {
  let result: boolean;
  return () => {
    if (typeof result === 'undefined') {
      try {
        result = !!window.localStorage;
      } catch (error) {
        result = false;
      }
    }
    return result;
  };
})();

export const browserInfo = getBrowserInfo();

/**
 * 获取浏览器及版本信息
 * @export
 * @returns {BrowserInfo}
 */
export function getBrowserInfo() {
  const browserInfoMap = new Map([
    [IS_FIREFOX, ['Firefox', FIREFOX_VERSION]],
    [IS_EDG, ['Edg', EDG_VERSION]],
    [IS_CHROME, ['Chrome', CHROME_VERSION]],
    [IS_IOS_CHROME, ['ChiOS', getBrowserVersion('CriOS')]],
    [IS_SAFARI && !IS_IOS_CHROME, ['Safari', SAFARI_VERSION]],
    [IS_TBS, ['TBS', TBS_VERSION]],
    [IS_XWEB, ['XWEB', XWEB_VERSION]],
    [IS_WECHAT && IS_IPHONE, ['WeChat', WECHAT_VERSION]],
    [IS_WQQB, ['QQ(Win)', WQQB_VERSION]],
    [IS_MQQB, ['QQ(Mobile)', MQQB_VERSION]],
    [IS_X5MQQB, ['QQ(Mobile X5)', MQQB_VERSION]],
    [IS_MACQQB, ['QQ(Mac)', MACQQB_VERSION]],
    [IS_IPADQQB, ['QQ(iPad)', IPADQQB_VERSION]],
    [IS_MIBROWSER, ['MI', MI_VERSION]],
    [IS_HUAWEIBROWSER, ['HW', HUAWEI_VERSION]],
    [IS_SAMSUNGBROWSER, ['Samsung', SAMSUNG_VERSION]],
    [IS_OPPOBROWSER, ['OPPO', OPPO_VERSION]],
    [IS_VIVOBROWSER, ['VIVO', VIVO_VERSION]],
    [IS_EDGE, ['EDGE', EDGE_VERSION]],
    [IS_SOGOUM, ['SogouMobile', SOGOUM_VERSION]],
    [IS_SOGOU, ['Sogou', SOGOU_VERSION]]
  ]);
  let name = 'unknown';
  let version = 'unknown';
  if (browserInfoMap.has(true)) {
    [name, version] = browserInfoMap.get(true)!;
  }
  return { name, version };
}

interface UserAgentData {
  platform: string;
  platformVersion: string;
  architecture: string;
  bitness: number;
  mobile: boolean;
  model?: string;
  fullVersionList?: { brand: string; version: string; }[];
}

let userAgentData: UserAgentData | null = null;

export function isMobile() {
  if (userAgentData && typeof userAgentData.mobile === 'boolean') return userAgentData.mobile;
  return IS_ANDROID || IS_IOS || IS_IPHONE || IS_IPAD || IS_OPENHARMONY;
}

export let UA_DATA_STRING = '';

export async function getUserAgentData(): Promise<UserAgentData | null> {
  if (userAgentData) return userAgentData;
  // @ts-ignore
  if (!navigator.userAgentData || typeof navigator.userAgentData.getHighEntropyValues !== 'function') return null;
  try {
    // @ts-ignore
    userAgentData = await navigator.userAgentData.getHighEntropyValues(['architecture', 'bitness', 'model', 'platformVersion', 'fullVersionList']);

    if (userAgentData && !UA_DATA_STRING) {
      UA_DATA_STRING = `UAData: ${userAgentData.platform}/${userAgentData.platformVersion}`;
      if (userAgentData.architecture && userAgentData.bitness) UA_DATA_STRING += ` ${userAgentData.architecture}/${userAgentData.bitness}`;
      if (userAgentData.mobile) UA_DATA_STRING += ' mobile';
      if (userAgentData.model) UA_DATA_STRING += ` model: ${userAgentData.model.replace(/\s+/g, '/')}`;
      if (userAgentData.fullVersionList) {
        UA_DATA_STRING += ` ${userAgentData.fullVersionList.filter((item: any) => item.brand !== 'Not/A)Brand').map((item: any) => `${item.brand}/${item.version}`)
          .join(',')}`;
      }
    }

    return userAgentData;
  } catch (error) {
    return null;
  }
}

let gpuInfo = '';

export function getGPUInfo() {
  try {
    if (gpuInfo) return gpuInfo;
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';
    // @ts-ignore
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      // @ts-ignore
      const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      // @ts-ignore
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      gpuInfo = `${vendor} ${renderer}`;
      return gpuInfo;
    }
    return '';
  } catch (error) {
    return '';
  }
}

export function isAMDGPU() {
  try {
    const gpuInfo = getGPUInfo();
    return gpuInfo.includes('AMD') || gpuInfo.includes('ATI');
  } catch (error) {
    return false;
  }
}

export function isNvidiaGPU() {
  try {
    const gpuInfo = getGPUInfo();
    return gpuInfo.includes('NVIDIA') || gpuInfo.includes('GeForce');
  } catch (error) {
    return false;
  }
}

export function isAppleSiliconGPU() {
  try {
    const gpuInfo = getGPUInfo();
    return gpuInfo.includes('Apple M');
  } catch (error) {
    return false;
  }
}

export function getDeviceModel() {
  return userAgentData?.model || getDeviceModelFromUA() || '';
}

export function getDeviceModelFromUA() {
  // 匹配 ; 之后, Build/ 之前的内容
  const regex = /;\s*([^;)]+)\s+Build\//;
  const match = USER_AGENT.match(regex);
  // match 结果是一个数组，第一个元素是完整匹配的字符串，第二个元素是第一个捕获组的内容
  const deviceModel = match?.[1] ? match[1].trim() : null;
  return deviceModel;
}

const OSNameMap = new Map([
  [IS_ANDROID, 'Android'],
  [IS_IOS, 'iOS'],
  [IS_WIN, 'Windows'],
  [IS_MAC, 'MacOS'],
  [IS_LINUX, 'Linux'],
  [IS_CHROME_OS, 'ChromeOS']
]);

export const getOSName = function () {
  if (OSNameMap.get(true)) {
    return OSNameMap.get(true);
  }
  if (userAgentData) return userAgentData.platform;
  return 'unknown';
};

export function getOSNumber() {
  if (IS_WIN) return 1;
  if (IS_ANDROID) return 2;
  if (IS_MAC) return 3;
  if (IS_IOS) return 4;
  if (IS_LINUX) return 5;
  if (IS_CHROME_OS) return 6;
  if (IS_OPENHARMONY) return 7;
  return 0;
}

// 获取浏览器内核对应的 number 枚举值，目前市场主流的浏览器内核只有 3 种：chromium、safari、firefox
export function getBrowserCoreNumber() {
  if (IS_WECHAT || IS_XWEB) return 4; // 由于需要单独统计微信内核的使用情况、微信内核特殊处理，返回 4 标识是微信内核。安卓微信是基于 chromium、iOS 微信是基于 safari 内核。
  if (IS_CHROMIUM_BASE) return 1;
  if (IS_SAFARI) return 2;
  if (IS_FIREFOX) return 3;
  return 0;
}


// 操作系统/操作系统版本/浏览器/浏览器版本/芯片架构
export const getOSString = () => {
  let osName = getOSName();
  if (userAgentData?.platformVersion) {
    osName += `/${userAgentData.platformVersion}`;
  } else if (IS_IOS) {
    osName += `/${IOS_VERSION}`;
  } else if (IS_ANDROID) {
    osName += `/${ANDROID_VERSION}`;
  }
  osName += `/${browserInfo.name}/${IS_SAFARI && !IS_IOS_CHROME ? browserInfo.version : browserInfo.version.split('.')[0]}`;

  if (userAgentData?.architecture) {
    osName += `/${userAgentData.architecture}`;
  }
  return osName;
};

export function getTerminalType() {
  if (IS_ANDROID) {
    return 4;
  } if (IS_IPHONE) {
    return 2;
  } if (IS_IPAD) {
    return 3;
  } if (IS_MAC) {
    return 12;
  } if (IS_WIN) {
    return 5;
  } if (IS_LINUX) {
    return 13;
  } if (IS_OPENHARMONY) {
    return 22;
  }
  // unknown
  return 1;
}

export function getOSType() {
  if (IS_ANDROID) {
    return 'Android';
  } if (IS_IPHONE) {
    return 'iPhone';
  } if (IS_IPAD) {
    return 'iPad';
  } if (IS_MAC) {
    return 'Mac';
  } if (IS_WIN) {
    return 'Windows';
  } if (IS_LINUX) {
    return 'Linux';
  }
  return 'unknown';
}
